import React, { useState } from "react";
import {
  Modal,
  ModalBody,
  ModalFooter,
  Row,
  Col,
  Button,
  Input,
  Label,
} from "reactstrap";
import { FormattedMessage, useIntl } from "react-intl";
import {
  CreateSchoolAndAssignProgram,
  AddClassToSchool,
  AddProgramToSchool,
  AddProgramLicenseToSchool,
  CreateNewTeacher,
} from "../../actions/superUserMutations";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { formatServiceError } from "../../Utilities/serviceErrorHelper";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { v4 as uuid } from "uuid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQueryClient } from "@tanstack/react-query";
import { GetOrientationsBySchoolId, GetAvailableProgramLicenses } from "../../actions/adminQueries";
import TeacherPermissionFields from "../Teacher/TeacherPermissionFields";

export default function CreateNewSchoolWizardModal({ isOpen, toggle }) {
  const intl = useIntl();
  const [page, setPage] = useState(1);
  const [fortnoxNumber, setFortnoxNumber] = useState("");
  const [selectedLicenses, setSelectedLicenses] = useState([]);
  const [errors, setErrors] = useState([]);
  const [classesForPost, setClassesForPost] = useState([]);
  const [newClass, setNewClass] = useState({ name: "" });
  const [createdSchool, setCreatedSchool] = useState(null);
  const [addedClasses, setAddedClasses] = useState([]);
  const [newTeacherToAdd, setNewTeacherToAdd] = useState({
    name: "",
    email: "",
    purchaser: false,
    fullAccess: false,
  });
  const [teachersForPost, setTeachersForPost] = useState([]);
  const [schoolAlreadyPosted, setSchoolAlreadyPosted] = useState(false);
  const [returnedPrograms, setReturnedPrograms] = useState([]);
  const availableLicenses = GetAvailableProgramLicenses();
  const orientations = GetOrientationsBySchoolId(createdSchool?.id);
  const handlePostSchoolWithLicense = useMutation({ mutationFn: CreateSchoolAndAssignProgram });
  const handlePostClassesToSchool = useMutation({ mutationFn: AddClassToSchool });
  const handlePostProgram = useMutation({ mutationFn: AddProgramToSchool });
  const handlePostProgramLicense = useMutation({ mutationFn: AddProgramLicenseToSchool });
  const handleCreateNewTeacher = useMutation({ mutationFn: CreateNewTeacher });
  const queryClient = useQueryClient();

  const validateStepAndGoToNext = () => {
    switch (page) {
      case 1:
        if (fortnoxNumber.length > 0) {
          setPage(page + 1);
          setErrors(errors.filter((err) => err !== "page1"));
        } else {
          setErrors([...errors, "page1"]);
        }
        break;
      default:
        break;
    }
  };

  const handleClose = (reloadSchools) => {
    setPage(1);
    setFortnoxNumber("");
    setSelectedLicenses([]);
    setErrors([]);
    setClassesForPost([]);
    setNewClass({ name: "" });
    setCreatedSchool(null);
    setNewTeacherToAdd({
      name: "",
      email: "",
      purchaser: false,
      fullAccess: false,
    });
    setTeachersForPost([]);
    setAddedClasses([]);
    setSchoolAlreadyPosted(false);
    setReturnedPrograms([]);
    toggle();
    if (reloadSchools) {
      queryClient.invalidateQueries({ queryKey: ["FetchSchoolsForAdmin"] });
    }
  };

  const postSchoolWithLicenses = (saveAndClose) => {
    let schoolData = null;
    if (!schoolAlreadyPosted) {
      handlePostSchoolWithLicense.mutate(
        {
          fortKnoxCustomerNumber: fortnoxNumber,
          licenses: selectedLicenses,
        },
        {
          onSuccess: (data) => {
            schoolData = data.data.result;
            toast.success(<FormattedMessage id="SchoolCreated" />);
            setCreatedSchool(data.data.result);
            setSchoolAlreadyPosted(true);
            postLicenses(schoolData, saveAndClose);
          },
          onError: (err) => {
            toast.warn(formatServiceError(intl, err?.response?.data));
          },
        }
      );
    } else {
      postLicenses(createdSchool, saveAndClose);
    }
  };

  const postLicenses = async (schoolData, saveAndClose) => {
    const licensePosts = Promise.allSettled(
      selectedLicenses.map((lic) =>
        handlePostProgramLicense
          .mutateAsync({
            license: lic,
            school: schoolData,
          })
          .then(() => lic),
      ),
    );
    saveAndClose ? handleClose(true) : setPage(page + 1);

    const postedLicenses = [];
    (await licensePosts).forEach((result) => {
      if (result.status === "fulfilled") {
        postedLicenses.push(result.value);
      } else {
        toast.warn(formatServiceError(intl, result.reason?.response?.data));
      }
    });
    if (postedLicenses.length === 0) {
      return;
    }
    toast.success(<FormattedMessage id="LicenseAdded" />);

    const results = await Promise.allSettled(
      postedLicenses.map((proglic) =>
        handlePostProgram.mutateAsync({
          license: proglic,
          school: schoolData,
        }),
      ),
    );
    const tempPrograms = [];
    results.forEach((result) => {
      if (result.status === "fulfilled") {
        tempPrograms.push(result.value.data.result);
      } else {
        toast.warn(formatServiceError(intl, result.reason?.response?.data));
      }
    });
    if (tempPrograms.length > 0) {
      toast.success(<FormattedMessage id="ProgramAdded" />);
    }
    setReturnedPrograms(tempPrograms);
  };

  const handlePostClasses = async () => {
    const promises = [];
    classesForPost.forEach((cl) => {
      const program = {
        id: cl.program.id,
        name: cl.program.name,
        costCenter: cl.program.costCenter,
        customer: createdSchool,
        classes: null,
      };
      promises.push(
        handlePostClassesToSchool.mutateAsync({
          name: cl.name,
          school: createdSchool,
          selectedProgramme: program,
          selectedOrientation:
            cl.orientation !== undefined ? cl.orientation : "",
        })
      );
    });
    const results = await Promise.allSettled(promises);
    const tempClasses = [];
    results.forEach((result) => {
      if (result.status === "fulfilled") {
        tempClasses.push(result.value.data.result);
      } else {
        toast.warn(formatServiceError(intl, result.reason?.response?.data));
      }
    });
    if (results.length > 0 && tempClasses.length === 0) {
      return;
    }
    toast.success(<FormattedMessage id="ClassesAdded" />);
    setAddedClasses(tempClasses);

    setPage(page + 1);
  };

  const handlePostTeachersAndClose = async () => {
    if (teachersForPost.some((teacher) => teacher.classes.length === 0)) {
      toast.warn(
        <FormattedMessage id="Views.Admin.CreateTeacherForm.Error.SelectClass" />,
      );
      return;
    }

    const promises = [];
    teachersForPost.forEach((teacher) => {
      const teacherToPost = {
        name: teacher.name,
        email: teacher.email,
        fullAccess: teacher.fullAccess,
        purchaser: teacher.purchaser,
        activatedAccount: false,
        aplSupervisorMail: null,
        classes: teacher.classes,
      };
      promises.push(
        handleCreateNewTeacher.mutateAsync({
          teacherToPost,
          classIds: teacher.classes.map((cl) => cl.value.id),
        }),
      );
    });
    const results = await Promise.allSettled(promises);
    results.forEach((result) => {
      if (result.status === "rejected") {
        toast.warn(formatServiceError(intl, result.reason?.response?.data));
      }
    });
    if (results.length > 0 && results.every((r) => r.status === "rejected")) {
      return;
    }
    toast.success(<FormattedMessage id="TeacherAdded" />);
    handleClose(true);
  };

  const renderPage = () => {
    switch (page) {
      case 1:
        return fortnoxNumberPage();
      case 2:
        return licensesPage();
      case 3:
        return selectedLicensescreateClasses();
      case 4:
        return connectTeachersToClasses();
      default:
        return fortnoxNumberPage();
    }
  };

  const handleSetFortnoxNumber = (number) => {
    if (number.length > 0) {
      setErrors(errors.filter((err) => err !== "page1"));
    }
    setFortnoxNumber(number);
  };

  const fortnoxNumberPage = () => {
    return (
      <>
        <p className="lead">
          <FormattedMessage id="StateFortnoxNumber" />
        </p>
        <Row>
          <Col>
            <Label for="fortnoxNumber">
              <FormattedMessage id="FortnoxNumber" />
            </Label>
          </Col>
          <Col>
            <Input
              type="text"
              style={{
                border: errors.includes("page1")
                  ? "1px solid red"
                  : "1px solid black",
              }}
              name="fortnoxNumber"
              value={fortnoxNumber}
              onChange={(event) => handleSetFortnoxNumber(event.target.value)}
            ></Input>
          </Col>
        </Row>
      </>
    );
  };

  const licensesPage = () => {
    return (
      <>
        <p className="lead">
          <FormattedMessage id="AddProgramLicenses" />
        </p>
        <Label for="licenseSelector">
          <FormattedMessage id="ChooseProgramLicensesToAdd" />
        </Label>
        <div>
          <FormattedMessage id="NoteThatSchoolWillGetLicenseAndProgram" />.
        </div>
        <Select
          isMulti={true}
          name="licenseSelector"
          options={(availableLicenses.data ?? []).map((option) => ({
            ...option,
            label: option.displayName,
            value: option,
            clearableValue: true,
          }))}
          onChange={(data) => setSelectedLicenses(data)}
        ></Select>
      </>
    );
  };

  const selectedLicensescreateClasses = () => {
    return (
      <>
        <p className="lead">
          <FormattedMessage id="AddClasses" />
        </p>
        <Row className="pt-2">
          <Col lg="2">
            <Label for="classNameForCreate">
              <FormattedMessage id="ClassName" />
            </Label>
          </Col>
          <Col>
            <Input
              name="classNameForCreate"
              type="text"
              value={newClass.name}
              onChange={(data) =>
                setNewClass({ ...newClass, name: data.target.value })
              }
            ></Input>
          </Col>
        </Row>
        <Row className="pt-2">
          <Col lg="2">
            <Label for="programForClass">
              <FormattedMessage id="Program" />
            </Label>
          </Col>
          <Col>
            <Select
              isMulti={false}
              name="programForClass"
              options={returnedPrograms.map((option) => ({
                ...option,
                label: option.name,
                value: option,
                clearableValue: true,
              }))}
              onChange={(data) => setNewClass({ ...newClass, program: data })}
            ></Select>
          </Col>
        </Row>
        <Row className="pt-2">
          <Col lg="2">
            <Label for="orientationForClass">
              <FormattedMessage id="Orientation" />
            </Label>
          </Col>
          <Col>
            <CreatableSelect
              name="selectedOrientation"
              isClearable
              placeholder={intl.formatMessage({ id: "ChooseOrientationOrCreate" })}
              noOptionsMessage={() => intl.formatMessage({ id: "NoAvailablePrograms" })}
              onChange={(e) =>
                e
                  ? setNewClass({ ...newClass, orientation: e.label })
                  : setNewClass({ ...newClass, orientation: null })
              }
              options={(orientations.data ?? []).map((option) => ({
                ...option,
                label: option.name,
                value: option.id.toString(),
                clearableValue: true,
              }))}
              formatCreateLabel={(userInput) =>
                intl.formatMessage({ id: "ClickHereToCreate" }, { userInput })
              }
            />
          </Col>
        </Row>
        <Row className="pt-2">
          <Col>
            <Button
              onClick={() => selectedLicenses.length > 0 && handleAddClass()}
              title={
                selectedLicenses.length < 1
                  ? intl.formatMessage({
                      id: "YouCannotAddAClassIfYouHaveNotAddedProgram",
                    })
                  : undefined
              }
              outline
              color="primary"
              style={{
                cursor: selectedLicenses.length < 1 ? "not-allowed" : "pointer",
              }}
            >
              <FormattedMessage id="AddClass" />
            </Button>
          </Col>
        </Row>
        {classesForPost.length > 0 && (
          <>
            <Row>
              <Col lg="1"></Col>
              <Col>
                <FormattedMessage id="Name" />
              </Col>
              <Col>
                <FormattedMessage id="Program" />
              </Col>
              <Col>
                <FormattedMessage id="Orientation" />
              </Col>
            </Row>
            {classesForPost.map((cl) => (
              <Row key={cl.id}>
                <Col lg="1">
                  <Button
                    color="danger"
                    outline={true}
                    size="sm"
                    onClick={() => handleRemoveClassFromAdd(cl)}
                  >
                    <FontAwesomeIcon icon="trash" />
                  </Button>
                </Col>
                <Col>{cl.name}</Col>
                <Col>{cl.program.name}</Col>
                <Col>{cl.orientation}</Col>
              </Row>
            ))}
          </>
        )}
      </>
    );
  };

  const connectTeachersToClasses = () => {
    return (
      <>
        <p className="lead">
          <FormattedMessage id="AddTeacher" />
        </p>
        <Row className="mt-2">
          <Col>
            <Label for="newTeacherName">
              <FormattedMessage id="Name" />
            </Label>
          </Col>
          <Col>
            <Label for="newTeacherEmail">
              <FormattedMessage id="Email" />
            </Label>
          </Col>
        </Row>
        <Row>
          <Col>
            <Input
              value={newTeacherToAdd.name}
              onChange={(data) =>
                setNewTeacherToAdd({
                  ...newTeacherToAdd,
                  name: data.target.value,
                })
              }
              type="text"
              placeholder={intl.formatMessage({ id: "Name" })}
              name="newTeacherName"
              style={
                errors.includes("teacherName")
                  ? { borderColor: "red" }
                  : { borderColor: "#c2cfd6" }
              }
            />
          </Col>

          <Col>
            <Input
              value={newTeacherToAdd.email}
              onChange={(data) =>
                setNewTeacherToAdd({
                  ...newTeacherToAdd,
                  email: data.target.value,
                })
              }
              type="text"
              placeholder="Epost"
              name="newTeacherEmail"
              style={
                errors.includes("teacherEmail")
                  ? { borderColor: "red" }
                  : { borderColor: "#c2cfd6" }
              }
            />
          </Col>
        </Row>
        <Row className="mt-1">
          <Col>
            <TeacherPermissionFields
              values={newTeacherToAdd}
              grantor={{ fullAccess: true, purchaser: true }}
              onToggle={(field, value) =>
                setNewTeacherToAdd({ ...newTeacherToAdd, [field]: value })
              }
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <Button
              onClick={() => handleCheckAndSetTeacherForAdd()}
              color="primary"
              outline
            >
              <FormattedMessage id="SaveTeacher" />
            </Button>
          </Col>
        </Row>
        <Row>
          <Col lg="1"></Col>
          <Col lg="3">
            <FormattedMessage id="Name" />
          </Col>
          <Col lg="3">
            <FormattedMessage id="Email" />
          </Col>
          <Col lg="5">
            <FormattedMessage id="Classes" />
          </Col>
        </Row>
        {teachersForPost &&
          teachersForPost.map((teacher) => {
            let selectedClasses = [];
            teacher.classes.forEach((cl) => {
              selectedClasses.push({
                label: cl.value.name + " -- " + cl.value.programme.name,
                value: cl.value,
              });
            });

            const options = [];
            addedClasses.forEach((cl) => {
              options.push({
                label: cl.name + " -- " + cl.programme.name,
                value: cl,
                clearableValue: true,
              });
            });
            return (
              <Row key={teacher.id}>
                <Col lg="1">
                  <Button
                    color="danger"
                    outline={true}
                    size="sm"
                    onClick={() => handleRemoveTeacherFromAdd(teacher)}
                  >
                    <FontAwesomeIcon icon="trash" />
                  </Button>
                </Col>
                <Col lg="3">
                  <p style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                    {teacher.name}
                  </p>
                </Col>
                <Col lg="3">
                  <p style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
                    {teacher.email}
                  </p>
                </Col>
                <Col lg="5">
                  <Select
                    isMulti={true}
                    name="programForClass"
                    value={selectedClasses}
                    options={options}
                    onChange={(data) =>
                      handleSetClassForNewTeacher(data, teacher)
                    }
                  ></Select>
                </Col>
              </Row>
            );
          })}
      </>
    );
  };

  const handleCheckAndSetTeacherForAdd = () => {
    var pattern = /^[\w.-]+@[\w.-]+\.\w+$/;
    if (!pattern.test(newTeacherToAdd.email)) {
      setErrors([...errors, "teacherEmail"]);
      return null;
    }
    if (newTeacherToAdd.name.length < 1) {
      setErrors([...errors, "teacherName"]);
      return null;
    }
    var excludedValues = ["teacherName", "teacherEmail"];

    var tempErrors = errors.filter(function (value) {
      return !excludedValues.includes(value);
    });
    setErrors(tempErrors);
    setTeachersForPost([
      ...teachersForPost,
      {
        id: uuid(),
        name: newTeacherToAdd.name,
        email: newTeacherToAdd.email,
        purchaser: newTeacherToAdd.purchaser,
        fullAccess: newTeacherToAdd.fullAccess,
        classes: [],
      },
    ]);
  };

  const handleRemoveTeacherFromAdd = (teacher) => {
    let tempTeachers = [...teachersForPost];
    tempTeachers.splice(
      tempTeachers.findIndex((t) => t.id === teacher.id),
      1
    );
    setTeachersForPost(tempTeachers);
  };

  const handleRemoveClassFromAdd = (cl) => {
    let tempClasses = [...classesForPost];
    tempClasses.splice(
      tempClasses.findIndex((t) => t.id === cl.id),
      1
    );
    setClassesForPost(tempClasses);
  };

  const handleSetClassForNewTeacher = (cl, teacher) => {
    let tempTeachers = [...teachersForPost];
    tempTeachers.find((t) => t.id === teacher.id).classes = cl;
    setTeachersForPost(tempTeachers);
  };

  const handleAddClass = () => {
    setClassesForPost([...classesForPost, { ...newClass, id: uuid() }]);
  };

  return (
    <Modal size="lg" isOpen={isOpen} toggle={() => handleClose()}>
      <ModalBody>{renderPage()}</ModalBody>
      <ModalFooter>
        <Button color="secondary" outline onClick={() => handleClose()}>
          <FormattedMessage id="Cancel" />
        </Button>
        {page === 2 && (
          <Button
            color="primary"
            outline
            onClick={() => postSchoolWithLicenses(true)}
          >
            <FormattedMessage id="SaveAndClose" />
          </Button>
        )}
        {page >= 2 && (
          <Button
            color="primary"
            outline
            onClick={() => (page >= 2 ? setPage(page - 1) : null)}
          >
            <FormattedMessage id="GoBack" />
          </Button>
        )}
        {page === 2 ? (
          <Button onClick={() => postSchoolWithLicenses(false)}>
            <FormattedMessage id="SaveAndContinue" />
          </Button>
        ) : page === 3 ? (
          <Button color="primary" outline onClick={() => handlePostClasses()}>
            <FormattedMessage id="SaveAndContinue" />
          </Button>
        ) : page === 4 ? (
          <Button
            color="primary"
            outline
            onClick={() => handlePostTeachersAndClose()}
          >
            <FormattedMessage id="SaveAndClose" />
          </Button>
        ) : (
          <Button
            color="primary"
            outline
            onClick={() => validateStepAndGoToNext()}
          >
            <FormattedMessage id="Next" />
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
}
